<template>
  <div class="api-edition-form">
    <v-alert
      v-model="alert"
      dismissible
      color="error"
      border="left"
      elevation="2"
      colored-border
      icon="priority_high"
    >An error happened while saving: {{errorDisplay(updateError)}}</v-alert>
    <v-row no-gutters>
      <v-btn class="my-4 mx-1" :color="editable ? 'error': 'primary'" @click="cancelEdition">
        <v-icon dark left>{{editable ? 'cancel' : 'mdi-pencil'}}</v-icon>
        {{ editable ? 'Cancel' : 'Edit' }}
      </v-btn>
      <v-btn class="my-4 mx-1" color="success" v-if="editable" @click="save">
        <v-icon dark left>save</v-icon>Save
      </v-btn>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" md="6">
        <v-text-field
          label="uuid"
          placeholder="uuid"
          outlined
          dense
          v-model="newUuid"
          disabled
          class="pa-1"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          label="id*"
          placeholder="ID..."
          outlined
          v-model="newId"
          :rules="[rules.min3, rules.startsWithAPI]"
          required
          dense
          :disabled="!editable"
          class="pa-1"
        ></v-text-field>
      </v-col>
      <v-col cols="12">
        <v-text-field
          label="name*"
          placeholder="Name..."
          outlined
          v-model="newName"
          :rules="[rules.min3]"
          required
          dense
          :disabled="!editable"
          class="pa-1"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-textarea
      label="description"
      placeholder="Description..."
      class="pa-1"
      rows="2"
      outlined
      :maxlength="130"
      dense
      no-resize
      :disabled="!editable"
      v-model="newDescription"
    ></v-textarea>
    <v-text-field
      label="url*"
      placeholder="URL..."
      outlined
      v-model="newRootUrl"
      dense
      hide-details
      :disabled="!editable"
      class="pa-1"
    ></v-text-field>
    <v-row no-gutters>
      <v-col cols="12" md="6">
        <h1 class="caption mx-2 ma-2">Redirect URIs</h1>
        <template v-for="(uri, index) in newRedirectUris">
          <v-text-field
            outlined
            placeholder="url"
            :key="'redirect-'+index"
            v-model="newRedirectUris[index]"
            append-icon="remove_circle"
            @click:append="newRedirectUris.splice(index, 1)"
            dense
            hide-details
            :disabled="!editable"
            class="mx-1 my-1"
          ></v-text-field>
        </template>
        <v-text-field
          outlined
          placeholder="url"
          v-model="additionalRedirectUri"
          append-outer-icon="add_box"
          @click:append-outer="addRedirectUri"
          dense
          :disabled="!editable"
          hide-details
          class="mx-1 my-1"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <h1 class="caption mx-2 ma-2">Web Origins</h1>
        <template v-for="(uri, index) in newWebOrigins">
          <v-text-field
            outlined
            placeholder="url"
            :key="'web-'+index"
            v-model="newWebOrigins[index]"
            append-icon="remove_circle"
            @click:append="newWebOrigins.splice(index, 1)"
            dense
            hide-details
            :disabled="!editable"
            class="mx-1 my-1"
          ></v-text-field>
        </template>
        <v-text-field
          outlined
          placeholder="url"
          v-model="additionalWebOrigin"
          append-outer-icon="add_box"
          @click:append-outer="addWebOrigins"
          dense
          :disabled="!editable"
          hide-details
          class="mx-1 my-1"
        ></v-text-field>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import mixin from "@/mixins";
import { mapState } from "vuex";
export default {
  mixins: [mixin],
  props: {
    uuid: { type: String },
    id: { type: String },
    name: { type: String },
    description: { type: String },
    rootUrl: { type: String },
    redirectUris: { type: Array },
    webOrigins: { type: Array }
  },
  data() {
    return {
      editable: false,
      alert: false,
      newUuid: "",
      newId: "",
      newName: "",
      newDescription: "",
      newRootUrl: "",
      newRedirectUris: [],
      newWebOrigins: [],
      additionalRedirectUri: "",
      additionalWebOrigin: "",
      rules: {
        min3: v =>
          v.length >= 3 ||
          "This field is required, and must contain 3 letters at least",
        startsWithAPI: v =>
          v.startsWith("api-") ||
          "This field is required, and must contain 3 letters at least"
      }
    };
  },
  computed: {
    ...mapState({
      updateError: state => state.apis.updateOne.error,
      updateLoading: state => state.apis.updateOne.pending
    })
  },
  watch: {
    uuid() {
      this.newUuid = this.uuid;
    },
    id() {
      this.newId = this.id;
    },
    name() {
      this.newName = this.name;
    },
    description() {
      this.newDescription = this.description;
    },
    rootUrl() {
      this.newRootUrl = this.rootUrl;
    },
    redirectUris() {
      this.newRedirectUris = [...this.redirectUris];
    },
    webOrigins() {
      this.newWebOrigins = [...this.webOrigins];
    },
    updateError() {
      if (this.updateError) {
        this.alert = true;
      } else {
        this.alert = false;
      }
    }
  },
  methods: {
    cancelEdition() {
      if (this.editable) {
        this.newUuid = this.uuid;
        this.newId = this.id;
        this.newName = this.name;
        this.newDescription = this.description;
        this.newRootUrl = this.rootUrl;
        this.newRedirectUris = [...this.redirectUris];
        this.newWebOrigins = [...this.webOrigins];
        this.additionalRedirectUri = "";
        this.additionalWebOrigin = "";
      }
      this.editable = !this.editable;
    },
    save() {
      this.$emit(
        "save",
        this.newId,
        this.newName,
        this.newDescription,
        this.newRootUrl,
        this.newRedirectUris,
        this.newWebOrigins
      );
    },
    saveCallback() {
      this.editable = false;
    },
    addRedirectUri() {
      this.newRedirectUris.push(this.additionalRedirectUri);
      this.additionalRedirectUri = "";
    },
    addWebOrigins() {
      this.newWebOrigins.push(this.additionalWebOrigin);
      this.additionalWebOrigin = "";
    }
  }
};
</script>

<style>
</style>